import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Users, UserPlus, Mail, Shield, Trash2, ArrowLeft,
  CheckCircle, Clock, XCircle, Loader2, Crown, Edit2,
  AlertCircle, Copy, Check
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from '../../components/ui/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeamPage = () => {
  const { toast } = useToast();
  const [members, setMembers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [teamInfo, setTeamInfo] = useState({ limit: 3, current_count: 0, can_invite: true });
  const [submitting, setSubmitting] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);
  
  const [inviteData, setInviteData] = useState({
    email: '',
    role: 'collaborator',
    message: ''
  });

  const roles = [
    { 
      id: 'admin', 
      label: 'Administrateur', 
      description: 'Accès complet sauf paramètres du compte',
      icon: Shield
    },
    { 
      id: 'accountant', 
      label: 'Comptable', 
      description: 'Factures, clients, exports et rapports',
      icon: Users
    },
    { 
      id: 'collaborator', 
      label: 'Collaborateur', 
      description: 'Création de factures, devis et clients',
      icon: Users
    }
  ];

  useEffect(() => {
    loadTeam();
  }, []);

  const loadTeam = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/team/members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMembers(data.members || []);
        setPendingInvitations(data.pending_invitations || []);
        setTeamInfo({
          limit: data.limit || 3,
          current_count: data.current_count || 0,
          can_invite: data.can_invite !== false
        });
      }
    } catch (error) {
      console.error('Error loading team:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteData.email.trim()) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/team/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(inviteData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: 'Invitation envoyée',
          description: `Un email a été envoyé à ${inviteData.email}`
        });
        setInviteData({ email: '', role: 'collaborator', message: '' });
        setShowInviteForm(false);
        loadTeam();
      } else {
        toast({
          title: 'Erreur',
          description: data.detail || 'Impossible d\'envoyer l\'invitation',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur de connexion',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/team/invitation/${invitationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({ title: 'Invitation annulée' });
        loadTeam();
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible d\'annuler l\'invitation',
        variant: 'destructive'
      });
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer ce membre ?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/team/member/${memberId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({ title: 'Membre retiré' });
        loadTeam();
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de retirer le membre',
        variant: 'destructive'
      });
    }
  };

  const copyInviteLink = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const getRoleBadge = (role) => {
    const styles = {
      owner: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Propriétaire' },
      admin: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Admin' },
      accountant: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Comptable' },
      collaborator: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Collaborateur' }
    };
    const style = styles[role] || styles.collaborator;
    return <Badge className={`${style.bg} ${style.text}`}>{style.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <Badge className="bg-emerald-100 text-emerald-700">Actif</Badge>;
    }
    if (status === 'pending') {
      return <Badge className="bg-amber-100 text-amber-700">En attente</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-700">Inactif</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/smartfacture">
            <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Retour
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Users className="h-6 w-6 text-blue-600" />
              Mon équipe
            </h1>
            <p className="text-slate-500 text-sm">
              {teamInfo.current_count} / {teamInfo.limit} membres
            </p>
          </div>
        </div>
        {teamInfo.can_invite && (
          <Button 
            onClick={() => setShowInviteForm(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="invite-btn"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Inviter un membre
          </Button>
        )}
      </div>

      {/* Usage indicator */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-700">Places utilisées</span>
            <span className="text-sm text-slate-600">
              {teamInfo.current_count} sur {teamInfo.limit}
            </span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: `${(teamInfo.current_count / teamInfo.limit) * 100}%` }}
            />
          </div>
          {!teamInfo.can_invite && (
            <p className="text-sm text-amber-600 mt-3 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Limite atteinte. Contactez-nous pour ajouter plus de membres.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Invite Form */}
      {showInviteForm && (
        <Card className="border-emerald-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-emerald-600" />
              Inviter un collaborateur
            </CardTitle>
            <CardDescription>
              Envoyez une invitation par email pour rejoindre votre équipe
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Adresse email
                </label>
                <Input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({...inviteData, email: e.target.value})}
                  placeholder="collaborateur@entreprise.com"
                  required
                  data-testid="invite-email"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Rôle
                </label>
                <div className="grid gap-3">
                  {roles.map(role => {
                    const Icon = role.icon;
                    return (
                      <div 
                        key={role.id}
                        onClick={() => setInviteData({...inviteData, role: role.id})}
                        className={`p-4 border rounded-xl cursor-pointer transition-all ${
                          inviteData.role === role.id 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`role-${role.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            inviteData.role === role.id ? 'bg-blue-100' : 'bg-slate-100'
                          }`}>
                            <Icon className={`h-5 w-5 ${
                              inviteData.role === role.id ? 'text-blue-600' : 'text-slate-500'
                            }`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-800">{role.label}</span>
                              {inviteData.role === role.id && (
                                <CheckCircle className="h-4 w-4 text-blue-600" />
                              )}
                            </div>
                            <p className="text-sm text-slate-500">{role.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Message personnalisé (optionnel)
                </label>
                <textarea
                  value={inviteData.message}
                  onChange={(e) => setInviteData({...inviteData, message: e.target.value})}
                  placeholder="Bienvenue dans l'équipe !"
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                  data-testid="invite-message"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowInviteForm(false)}
                >
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="send-invite-btn"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  Envoyer l'invitation
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Members List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Membres de l'équipe</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun membre dans l'équipe</p>
              <p className="text-sm text-slate-400 mt-1">
                Invitez vos collaborateurs pour travailler ensemble
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {members.map(member => (
                <div 
                  key={member.id}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-xl"
                  data-testid={`member-${member.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-medium">
                      {member.name ? member.name.charAt(0).toUpperCase() : member.email.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">
                          {member.name || member.email}
                        </span>
                        {member.role === 'owner' && (
                          <Crown className="h-4 w-4 text-amber-500" />
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getRoleBadge(member.role)}
                    {getStatusBadge(member.status)}
                    {member.role !== 'owner' && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleRemoveMember(member.id)}
                        className="text-slate-400 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              Invitations en attente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingInvitations.map(inv => (
                <div 
                  key={inv.id}
                  className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-xl"
                >
                  <div>
                    <p className="font-medium text-slate-800">{inv.email}</p>
                    <p className="text-sm text-slate-500">
                      Expire le {new Date(inv.expires_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getRoleBadge(inv.role)}
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleCancelInvitation(inv.id)}
                      className="text-slate-400 hover:text-red-600"
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Roles Info */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Permissions par rôle</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {roles.map(role => {
              const Icon = role.icon;
              return (
                <div key={role.id} className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="h-5 w-5 text-slate-600" />
                    <span className="font-medium text-slate-800">{role.label}</span>
                  </div>
                  <p className="text-sm text-slate-500">{role.description}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamPage;
